import {
  listallComments,
  ListallCommentsResponse,
  ListallCommentsData,
  createaNewComment,
  CreateaNewCommentData,
  CreateaNewCommentResponse,
  getCommentDetails,
  GetCommentDetailsResponse,
  deleteComment,
  DeleteCommentData,
  DeleteCommentResponse,
  updateComment,
  UpdateCommentData,
  UpdateCommentResponse,
} from '@data-access/connect/tc-tcps';
import type { Client } from '@data-access/connect/tc-issue-management';
import {
  useQuery,
  useMutation,
  UseQueryResult,
  UseMutationResult,
  QueryClient,
} from '@tanstack/solid-query';
import { Accessor } from 'solid-js';

export class CommentApiService {
  constructor(
    private readonly client: Client,
    private readonly queryClient: QueryClient
  ) {}

  // Query for listing all comments
  public getCommentsQuery(
    options: Accessor<Omit<ListallCommentsData, 'client'>>
  ): UseQueryResult<ListallCommentsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['comments', options()],
      queryFn: async (): Promise<ListallCommentsResponse> => {
        const response = await listallComments({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...options(),
        });

        if (response.error) {
          throw new Error('Failed to list comments');
        }

        return response.data!;
      },
      enabled: true,
    }));
  }

  // Query for getting comment details
  public getCommentDetailsQuery(
    commentId: Accessor<string | undefined>
  ): UseQueryResult<GetCommentDetailsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['comment-details', commentId()],
      queryFn: async (): Promise<GetCommentDetailsResponse> => {
        const id = commentId();
        if (!id) {
          throw new Error('Comment ID is required');
        }

        const response = await getCommentDetails({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          path: { commentId: id },
        });

        if (response.error) {
          throw new Error('Failed to get comment details');
        }

        return response.data!;
      },
      enabled: !!commentId(),
    }));
  }

  // Mutation for creating a new comment
  public createCommentMutation(): UseMutationResult<
    CreateaNewCommentResponse,
    Error,
    CreateaNewCommentData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: CreateaNewCommentData
      ): Promise<CreateaNewCommentResponse> => {
        const response = await createaNewComment({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to create comment');
        }

        return response.data!;
      },
      onSuccess: () => {
        // Invalidate comments list query
        void this.queryClient.invalidateQueries({
          queryKey: ['comments'],
        });
      },
    }));
  }

  // Mutation for updating a comment
  public updateCommentMutation(): UseMutationResult<
    UpdateCommentResponse,
    Error,
    UpdateCommentData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: UpdateCommentData
      ): Promise<UpdateCommentResponse> => {
        const response = await updateComment({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to update comment');
        }

        return response.data!;
      },
      onSuccess: (_, variables) => {
        // Invalidate comments list and specific comment details
        void this.queryClient.invalidateQueries({
          queryKey: ['comments'],
        });
        void this.queryClient.invalidateQueries({
          queryKey: ['comment-details', variables.path.commentId],
        });
      },
    }));
  }

  // Mutation for deleting a comment
  public deleteCommentMutation(): UseMutationResult<
    DeleteCommentResponse,
    Error,
    DeleteCommentData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: DeleteCommentData
      ): Promise<DeleteCommentResponse> => {
        const response = await deleteComment({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to delete comment');
        }

        return response.data!;
      },
      onSuccess: (_, variables) => {
        // Invalidate comments list and remove specific comment details from cache
        void this.queryClient.invalidateQueries({
          queryKey: ['comments'],
        });
        this.queryClient.removeQueries({
          queryKey: ['comment-details', variables.path.commentId],
        });
      },
    }));
  }
}
